import { AxiosResponse } from 'axios'
import {
  EmptyApiResult,
  DiagnosticConfigurationViewModel,
  LocationSharingConfigurationViewModel,
  ApiResult,
  ImportConfigurationViewModel,
  OptionalWithAlternateValue,
  CertificateConfigurationOverrides,
  SupportFederatedAuthenticationConfiguration,
} from '../api'
import { useApiClient } from './useApiClient'
import { createFormDataFromModel } from '../utils'

export const useDiagnosticsApi = () => {
  const apiClient = useApiClient()

  const updateDiagnosticsConfiguration = (
    values: DiagnosticConfigurationViewModel,
  ) => {
    const data = createFormDataFromModel(values)
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: 'api/diagnostics/configuration',
      method: 'post',
      data,
      withCredentials: true,
    })
  }

  const getDiagnosticsConfiguration = () => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<DiagnosticConfigurationViewModel>>
    >({
      url: 'api/diagnostics/configuration',
      method: 'get',
      withCredentials: true,
    })
  }

  const updateCertificateConfiguration = (
    values: CertificateConfigurationOverrides,
  ) => {
    const data = createFormDataFromModel(values)
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: 'api/diagnostics/certificate/configuration',
      method: 'put',
      data,
      withCredentials: true,
    })
  }

  const getCertificateConfiguration = () => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CertificateConfigurationOverrides>>
    >({
      url: 'api/diagnostics/certificate/configuration',
      method: 'get',
      withCredentials: true,
    })
  }

  const updateSupportFederatedAuthenticationConfiguration = (
    values: SupportFederatedAuthenticationConfiguration,
  ) => {
    const data = createFormDataFromModel(values)
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: 'api/diagnostics/supportfederatedauthentication/configuration',
      method: 'put',
      data,
      withCredentials: true,
    })
  }

  const getSupportFederatedAuthenticationConfiguration = () => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<SupportFederatedAuthenticationConfiguration>>
    >({
      url: 'api/diagnostics/supportfederatedauthentication/configuration',
      method: 'get',
      withCredentials: true,
    })
  }

  const getImportConfiguration = () => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<ImportConfigurationViewModel>>
    >({
      url: 'api/diagnostics/import/configuration',
      method: 'get',
      withCredentials: true,
    })
  }

  const updateImportConfiguration = (
    request: OptionalWithAlternateValue<ImportConfigurationViewModel, null>,
  ) => {
    const data = request
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: 'api/diagnostics/import/configuration',
      method: 'put',
      data,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    })
  }

  const getLocationSharingConfiguration = () => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<LocationSharingConfigurationViewModel>>
    >({
      url: 'api/diagnostics/locationsharing/configuration',
      method: 'get',
      withCredentials: true,
    })
  }

  const updateLocationSharingConfiguration = (
    request: OptionalWithAlternateValue<
      LocationSharingConfigurationViewModel,
      null
    >,
  ) => {
    const data = request
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: 'api/diagnostics/locationsharing/configuration',
      method: 'put',
      data,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    })
  }

  return {
    updateDiagnosticsConfiguration,
    getDiagnosticsConfiguration,
    getImportConfiguration,
    updateImportConfiguration,
    getLocationSharingConfiguration,
    updateLocationSharingConfiguration,
    getCertificateConfiguration,
    updateCertificateConfiguration,
    getSupportFederatedAuthenticationConfiguration,
    updateSupportFederatedAuthenticationConfiguration,
  }
}
